import type { TFunction } from 'i18next'

import { refusalMessage } from '@/features/cases/lib/refusal'
import { receivableLabel, targetLabelOf } from '@/features/finance/allocationLabel'
import { ApiError } from '@/lib/api'
import { formatMoneyString } from '@/lib/format'
import { fieldErrors } from '@/lib/problems'
import type { CaseReceivables, Payment } from '@/types/domain'

/* A refund to the client is never a supplier payment (S-22f).
 *
 * The refund is `direction = 'out'` with a `clientId` — nothing else marks it,
 * on the server or here. Its money is the client's (`clientTotals.refunded`),
 * so the server refuses to allocate it to a cost line (422 `targetKind:
 * refund`) and refuses an amend that would turn an outbound payment already
 * allocated to costs into one (422 `clientId: allocated`).
 *
 * Both are worded HERE, by the payment sheets, rather than in the case's
 * `refusalMessage`: they are the payment's own rules, and that function would
 * word them as «check the highlighted fields» over a sheet with nothing
 * highlighted. A module rather than a component file, because a component file
 * may export only components. */

/** Whether the payment is a refund to the client. `false` for a party withheld
 *  from the reader (AD-6: neither id arrives) — the server's 422 is then the
 *  only word, which is what the wrapper below is for. */
export function isRefund(payment: Pick<Payment, 'direction' | 'clientId' | 'counterpartyId'>): boolean {
  return payment.direction === 'out' && payment.clientId !== undefined
}

/** The payment sheets' refusal: the two refund codes in the reader's words,
 *  everything else as the case refusal words it. */
export function paymentRefusalMessage(
  error: unknown,
  t: TFunction,
  receivables?: CaseReceivables,
): string {
  const allocation = allocationRefusal(error, t, receivables)
  if (allocation !== undefined) return allocation.message
  const codes = fieldErrors(error)
  if (codes.targetKind?.includes('refund')) return t('finance.allocations.refundNotCost')
  if (codes.clientId?.includes('allocated')) return t('finance.payments.party.refundAllocated')
  return refusalMessage(error, t)
}

/* ── An allocation refused by a ceiling or a replaced tranche (S-21) ─────────
 *
 * Two 409s name the document they refused and what it can still take, as typed
 * problem members (`lib/api.ts` `Problem`): `allocation-over-ceiling` — the
 * split is more than the PAYMENT has left (`ceiling: 'payment'`, `remaining`
 * in the payment's currency) or more than the DOCUMENT still takes
 * (`ceiling: 'target'`, `remaining` in USD, with the document's number or the
 * tranche's place) — and `allocation-target-closed`, a tranche an issued
 * invoice has replaced (`replaced_by`). They are worded HERE in the reader's
 * words, never as the generic «запись недоступна»: which document, how much.
 *
 * On a payment ceiling the server has not read the document yet, so it names
 * it by kind and id only; the caller's receivables read supplies the label. */

/** The refusal worded, and the id of the row it names so the sheet can
 *  outline that row's input. `undefined` for any other refusal. */
export interface AllocationRefusal {
  message: string
  targetId?: string
}

export function allocationRefusal(
  error: unknown,
  t: TFunction,
  receivables?: CaseReceivables,
): AllocationRefusal | undefined {
  if (!(error instanceof ApiError)) return undefined
  const problem = error.problem
  if (error.code !== 'allocation-over-ceiling' && error.code !== 'allocation-target-closed') {
    return undefined
  }

  const row = receivables?.items.find((item) => item.id === problem.target_id)
  const target =
    targetLabelOf(t, problem.target_kind ?? '', {
      number: problem.target_number,
      sequence: problem.target_sequence,
      of: problem.target_of,
    }) ??
    (row ? receivableLabel(t, row) : undefined) ??
    t('finance.clientPayment.refusal.unnamed')
  const targetId = problem.target_id

  if (error.code === 'allocation-target-closed') {
    return {
      message: t('finance.clientPayment.refusal.targetClosed', {
        target,
        number: problem.replaced_by ?? '',
      }),
      targetId,
    }
  }

  const remaining = problem.remaining
    ? formatMoneyString(problem.remaining.amount, problem.remaining.currency)
    : ''
  if (problem.ceiling === 'target') {
    return { message: t('finance.clientPayment.refusal.overTarget', { target, remaining }), targetId }
  }
  return {
    message: row || problem.target_number || problem.target_sequence !== undefined
      ? t('finance.clientPayment.refusal.overPayment', { target, remaining })
      : t('finance.clientPayment.refusal.overPaymentUnnamed', { remaining }),
    targetId,
  }
}
